import React from "react";
import { Card, CardHeader, CardBody, CardFooter, Row, Col, Button, Badge, FormCheckbox, FormSelect, ButtonGroup } from "shards-react";
import axios from "axios";

import baseURL from "../common/axios-config";
import { buildDougsSnippet, copyToClipboard } from "./dougsSnippet";

const PAGE_SIZE = 20;

function buildPayload(inv) {
	const p = {
		subject: inv.subject,
		lines: [
			{
				title: inv.line_title,
				quantity: inv.credit,
				unitAmount: inv.unit_amount,
				vatRate: 0.2,
				discount: inv.discount_pct,
			},
		],
	};
	if (inv.dougsClient) p.client = inv.dougsClient;
	else p.clientSearch = inv.clientSearch;
	return p;
}

export default class ListInvoices extends React.Component {
	constructor(props) {
		super(props);
		this.state = {
			invoices: [],
			totalUnpaid: 0,
			filter: "all", // "all", "paid", "unpaid"
			currentPage: 1,
			copied: {}, // { [invoiceId]: true } transient "copied" feedback
		};
	}

	componentDidMount() {
		this.loadInvoices();
	}

	loadInvoices() {
		axios(baseURL + "/monthlyBilling-getInvoices", {
			method: "POST",
			withCredentials: true,
		}).then((res) => {
			if (res.data.result === true) {
				this.setState({
					invoices: res.data.invoices,
					totalUnpaid: res.data.totalUnpaid || 0,
				});
			}
		});
	}

	togglePaid(id, currentPaid) {
		axios(baseURL + "/monthlyBilling-updateInvoicePaid", {
			method: "POST",
			withCredentials: true,
			data: { id: id, paid: !currentPaid },
		}).then((res) => {
			if (res.data.result === true) {
				this.loadInvoices();
			}
		});
	}

	copyCode(id) {
		axios(baseURL + "/monthlyBilling-getDougsPayload", {
			method: "POST",
			withCredentials: true,
			data: { id: id },
		}).then((res) => {
			if (res.data.result === true && res.data.invoice) {
				copyToClipboard(buildDougsSnippet([buildPayload(res.data.invoice)])).then(() => {
					this.setState((s) => ({ copied: { ...s.copied, [id]: true } }));
					setTimeout(() => {
						this.setState((s) => {
							const c = { ...s.copied };
							delete c[id];
							return { copied: c };
						});
					}, 2500);
				});
			}
		});
	}

	deleteInvoice(id) {
		if (!window.confirm("Supprimer cette facture du suivi local ? (n'affecte pas Dougs)")) return;
		axios(baseURL + "/monthlyBilling-deleteInvoice", {
			method: "POST",
			withCredentials: true,
			data: { id: id },
		}).then((res) => {
			if (res.data.result === true) {
				this.loadInvoices();
			}
		});
	}

	toggleRelance(id, currentRelance) {
		axios(baseURL + "/monthlyBilling-updateInvoiceRelance", {
			method: "POST",
			withCredentials: true,
			data: { id: id, relance: !currentRelance },
		}).then((res) => {
			if (res.data.result === true) {
				this.loadInvoices();
			}
		});
	}

	getFilteredInvoices() {
		const { invoices, filter } = this.state;
		if (filter === "paid") return invoices.filter((i) => i.paid);
		if (filter === "unpaid") return invoices.filter((i) => !i.paid);
		return invoices;
	}

	exportCSV() {
		const rows = this.getFilteredInvoices();
		const escape = (value) => {
			const str = value === null || value === undefined ? "" : String(value);
			return /[";\n]/.test(str) ? `"${str.replace(/"/g, '""')}"` : str;
		};
		const header = ["Référence", "Nom du fichier", "E-mail", "Client", "Date", "Crédits", "Prix (€)", "Payé", "Relancé"];
		const lines = [header.join(";")];
		rows.forEach((element) => {
			lines.push(
				[
					element.reference || "",
					element.name || "",
					element.email,
					element.client || "",
					(element.date || "").trim(),
					element.credit,
					String(element.price).replace(".", ","),
					element.paid ? "Oui" : "Non",
					element.relance ? "Oui" : "Non",
				]
					.map(escape)
					.join(";")
			);
		});

		// BOM UTF-8 pour qu'Excel ouvre correctement les accents
		const csv = "﻿" + lines.join("\n");
		const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
		const url = URL.createObjectURL(blob);
		const link = document.createElement("a");
		const stamp = new Date().toISOString().slice(0, 10);
		const suffix = this.state.filter === "paid" ? "_payees" : this.state.filter === "unpaid" ? "_non-payees" : "";
		link.href = url;
		link.download = `factures${suffix}_${stamp}.csv`;
		document.body.appendChild(link);
		link.click();
		document.body.removeChild(link);
		URL.revokeObjectURL(url);
	}

	render() {
		const { filter, currentPage } = this.state;
		const filtered = this.getFilteredInvoices();
		const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
		const page = Math.min(currentPage, totalPages);
		const paginated = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

		return (
			<Card small className="mb-4">
				<CardHeader className="border-bottom d-flex justify-content-between align-items-center">
					<h6 className="m-0">{this.props.title}</h6>
					<div className="d-flex align-items-center">
						<Button
							size="sm"
							theme="accent"
							className="mr-3"
							disabled={filtered.length === 0}
							onClick={() => this.exportCSV()}
						>
							Exporter CSV
						</Button>
						<span className="mr-2" style={{ fontSize: "0.85rem", whiteSpace: "nowrap" }}>Filtre :</span>
						<FormSelect
							size="sm"
							style={{ maxWidth: "160px" }}
							value={filter}
							onChange={(e) => this.setState({ filter: e.target.value, currentPage: 1 })}
						>
							<option value="all">Toutes</option>
							<option value="unpaid">Non payées</option>
							<option value="paid">Payées</option>
						</FormSelect>
					</div>
				</CardHeader>
				<CardBody className="p-0 overflow-auto" style={{ borderBottomRightRadius: ".625rem", borderBottomLeftRadius: ".625rem" }}>
					<Row className="my-auto">
						<Col sm="12">
							<table className="table mb-0">
								<thead className="bg-light">
									<tr>
										<th scope="col" className="border-0">#</th>
										<th scope="col" className="border-0">Référence</th>
										<th scope="col" className="border-0">E-mail</th>
										<th scope="col" className="border-0">Client</th>
										<th scope="col" className="border-0">Date</th>
										<th scope="col" className="border-0">Crédits</th>
										<th scope="col" className="border-0">Prix</th>
										<th scope="col" className="border-0">Payé</th>
										<th scope="col" className="border-0">Relancé</th>
										<th scope="col" className="border-0">Nom du fichier</th>
										<th scope="col" className="border-0">Télécharger</th>
							<th scope="col" className="border-0"></th>
									</tr>
								</thead>
								<tbody>
									{paginated.map((element, idx) => (
										<tr key={element.ID}>
											<th>{(page - 1) * PAGE_SIZE + idx + 1}</th>
											<td style={{ fontWeight: 500, whiteSpace: "nowrap" }}>{element.reference || "-"}</td>
											<td>{element.email}</td>
											<td>{element.client || "-"}</td>
											<td>{element.date}</td>
											<td>{element.credit}</td>
											<td>{element.price} €</td>
											<td>
												<FormCheckbox
													toggle
													small
													checked={element.paid}
													onChange={() => this.togglePaid(element.ID, element.paid)}
												>
													{element.paid ? (
														<Badge theme="success">Payé</Badge>
													) : (
														<Badge theme="danger">En attente</Badge>
													)}
												</FormCheckbox>
											</td>
											<td>
												<FormCheckbox
													toggle
													small
													checked={element.relance}
													onChange={() => this.toggleRelance(element.ID, element.relance)}
												>
													{element.relance ? (
														<Badge theme="success">Relancé</Badge>
													) : (
														<Badge theme="secondary">Non</Badge>
													)}
												</FormCheckbox>
											</td>
											<td style={{ fontSize: "0.8rem", maxWidth: "260px", wordBreak: "break-all" }}>
												{element.name || "-"}
											</td>
											<td>
												{element.pdf ? (
													<a
														href={baseURL + "/monthlyBilling-downloadInvoice/" + element.ID}
														target="_blank"
														rel="noopener noreferrer"
													>
														<Button size="sm" theme="info">
															<i className="material-icons">cloud_download</i>
														</Button>
													</a>
												) : (
													"-"
												)}
											</td>
								<td style={{ whiteSpace: "nowrap" }}>
										{element.type === 2 && (
											<Button size="sm" theme={this.state.copied[element.ID] ? "success" : "accent"} outline className="mr-1" title="Copier le code de génération Dougs" onClick={() => this.copyCode(element.ID)}>
												<i className="material-icons">{this.state.copied[element.ID] ? "check" : "content_copy"}</i>
											</Button>
										)}
										<Button size="sm" theme="danger" outline onClick={() => this.deleteInvoice(element.ID)}>
											<i className="material-icons">delete</i>
										</Button>
									</td>
										</tr>
									))}
									{paginated.length === 0 && (
										<tr>
											<td colSpan="12" className="text-center py-3 text-muted">
												Aucune facture {filter === "paid" ? "payée" : filter === "unpaid" ? "en attente" : ""}
											</td>
										</tr>
									)}
								</tbody>
							</table>
						</Col>
					</Row>
				</CardBody>
				<CardFooter className="d-flex justify-content-between align-items-center">
					<div className="d-flex align-items-center">
						{totalPages > 1 && (
							<ButtonGroup size="sm">
								<Button
									theme="white"
									disabled={page <= 1}
									onClick={() => this.setState({ currentPage: page - 1 })}
								>
									&laquo; Précédent
								</Button>
								{Array.from({ length: totalPages }, (_, i) => i + 1)
									.filter((p) => p === 1 || p === totalPages || Math.abs(p - page) <= 2)
									.reduce((acc, p, i, arr) => {
										if (i > 0 && p - arr[i - 1] > 1) {
											acc.push(<Button key={"dots-" + p} theme="white" disabled>...</Button>);
										}
										acc.push(
											<Button
												key={p}
												theme={p === page ? "primary" : "white"}
												onClick={() => this.setState({ currentPage: p })}
											>
												{p}
											</Button>
										);
										return acc;
									}, [])}
								<Button
									theme="white"
									disabled={page >= totalPages}
									onClick={() => this.setState({ currentPage: page + 1 })}
								>
									Suivant &raquo;
								</Button>
							</ButtonGroup>
						)}
						<span className="ml-3 text-muted" style={{ fontSize: "0.85rem" }}>
							{filtered.length} facture{filtered.length > 1 ? "s" : ""}
						</span>
					</div>
					<div className="p-2" style={{ fontSize: "1.1rem" }}>
						<strong>Total en attente de règlement : </strong>
						<Badge theme="danger" style={{ fontSize: "1rem", padding: "6px 12px" }}>
							{this.state.totalUnpaid.toFixed(2)} €
						</Badge>
					</div>
				</CardFooter>
			</Card>
		);
	}
}
